import * as React from 'react';
import { observer } from 'mobx-react';
import { action, makeObservable, observable } from 'mobx';
import * as PopperJS from '@popperjs/core';
import ReactDOM from 'react-dom';
import './tooltip.scss';

const CONTAINER_ID = 'tooltip-container';

export class ToolTipContainer extends React.Component {
    render() {
        return <div id={CONTAINER_ID} />;
    }
}

type ToolTipItemProps = {
    targetId: string;
    text?: string | JSX.Element;
    className?: string;
    placement?: PopperJS.Placement;
};

@observer
export class ToolTipItem extends React.Component<ToolTipItemProps> {
    @observable private _showPortal: boolean = false;
    @observable private _tooltipInstance: PopperJS.Instance | null = null;
    private _tooltipRef: React.RefObject<HTMLDivElement> = React.createRef();
    private _tooltipArrowRef: React.RefObject<HTMLDivElement> = React.createRef();
    private _targetElement: HTMLElement | null = null;
    private _timer: number | null = null;
    private _popperMouseOver: boolean = false;

    constructor(args: ToolTipItemProps){
        super(args);
        makeObservable(this);
    }

    componentDidMount() {
        this._checkTargetElement();
    }

    componentWillUnmount() {
        this._stopTimer();
        if (this._targetElement) {
            this._targetElement.removeEventListener('mouseenter', this._onContainerMouseOver);
            this._targetElement.removeEventListener('mouseleave', this._onContainerMouseOut);
        }
        this._hide();
    }

    private _checkTargetElement = () => {
        this._targetElement = document.getElementById(this.props.targetId);
        if (this._targetElement) {
            this._targetElement.addEventListener('mouseenter', this._onContainerMouseOver);
            this._targetElement.addEventListener('mouseleave', this._onContainerMouseOut);
        } else {
            this._stopTimer();
            this._timer = window.setTimeout(this._checkTargetElement, 100);
        }
    };

    private _popperMouseEnter = () => {
        this._popperMouseOver = true;
    };

    private _popperMouseLeave = () => {
        this._popperMouseOver = false;
        this._closePopper();
    };

    @action.bound
    private _createPopper() {
        if (!this._targetElement || !this._tooltipRef.current) return;
        const { placement } = this.props;
        this._tooltipInstance = PopperJS.createPopper(
            this._targetElement,
            this._tooltipRef.current,
            {
                placement: placement || 'right',
                modifiers: [
                    {
                        name: 'arrow',
                        options: {
                            element: this._tooltipArrowRef.current
                        }
                    },
                    {
                        name: 'events',
                        phase: 'main',
                        enabled: true,
                        effect: (eff) => {
                            eff.state.elements.popper.addEventListener('mouseenter', this._popperMouseEnter);
                            eff.state.elements.popper.addEventListener('mouseleave', this._popperMouseLeave);
                            return () => {
                                eff.state.elements.popper.removeEventListener('mouseenter', this._popperMouseEnter);
                                eff.state.elements.popper.removeEventListener('mouseleave', this._popperMouseLeave);
                            };
                        }
                    }
                ]
            }
        );
    }

    @action.bound
    private _destroyPopper() {
        if (this._tooltipInstance) {
            this._tooltipInstance = null;
        }
    }

    private _closePopper() {
        this._stopTimer();
        this._timer = window.setTimeout(() => {
            if (!this._popperMouseOver) this._hide();
        }, 100);
    }

    @action.bound
    private _onContainerMouseOut = () => {
        this._closePopper();
    };

    @action.bound
    private _onContainerMouseOver() {
        this._stopTimer();
        this._showPortal = true;
        this._timer = window.setTimeout(this._createPopper, 250);
    }

    @action.bound
    private _hide() {
        this._stopTimer();
        this._destroyPopper();
        this._showPortal = false;
    }

    private _stopTimer() {
        if (this._timer) {
            window.clearTimeout(this._timer);
            this._timer = null;
        }
    }

    render() {
        const { className, children, text } = this.props;
        const cls = ['tooltip popper'];
        className && cls.push(className);
        this._tooltipInstance && cls.push('show');
        return (
            <ToolTipPortal showPortal={this._showPortal}>
                <div className={cls.join(' ')} ref={this._tooltipRef}>
                    <div className="tooltip-inner">
                        {text}
                        {children}
                    </div>
                    <div className="arrow" data-popper-arrow ref={this._tooltipArrowRef} />
                </div>
            </ToolTipPortal>
        );
    }
}

type ToolTipPortalProps = {
    showPortal: boolean;
};

class ToolTipPortal extends React.Component<ToolTipPortalProps, {}> {
    render() {
        if (!this.props.showPortal) return null;

        const portalContainer = document.getElementById(CONTAINER_ID);
        return portalContainer ? ReactDOM.createPortal(this.props.children, portalContainer) : null;
    }
}
